import { adminEmail, mimeTypes } from '@/shared/constants';
import { type DriveFile } from '@/shared/DriveTypes';
import { Close, InfoOutlined, OpenInNew } from '@mui/icons-material';
import {
  Avatar,
  CircularProgress,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { match } from 'ts-pattern';
import { Link, useLocation } from 'wouter';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import CopyToClipboard from '~/elements/CopyToClipboard/CopyToClipboard';
import Title from '~/elements/Title/Title';
import * as DriveAPI from '~/utils/DriveAPI';
import { DrivePicker, type PickerFile } from '~/utils/DrivePicker';
import { useAuth, useRootFolder, useWikis } from '~/utils/hooks/hooks';
import { useStore } from '~/utils/store';
import { AvatarMenu } from '../AvatarMenu/AvatarMenu';
import * as formStyles from '../Form.css';
import * as inviteStyles from '../Invite/Invite.css';
import * as styles from './Create.css';

const NumberCircle = ({ n, className }: { n: number; className?: string }) => (
  <svg className={className} focusable="false" viewBox="0 0 24 24" aria-hidden="true" width="24" height="24">
    <path fill="none" d="M0 0h24v24H0V0z" />
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
      fill="currentColor"
    />
    <text x="12" y="16" textAnchor="middle" fontSize="11.5" fontWeight="700" fill="currentColor">
      {n}
    </text>
  </svg>
);

export const Create = () => {
  const [, navigate] = useLocation();
  const { mutate } = useSWRConfig();
  const { data: rootFolder } = useRootFolder();
  const { data: wikis = [], mutate: mutateWikis } = useWikis();
  const { data: user } = useAuth();
  const setToast = useStore((store) => store.setToast);

  const getInitialSelection = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('existing')) return 'existing';
    if (params.has('shared')) return 'shared';
    return 'new';
  };

  const [selection, setSelection] = useState<'new' | 'existing' | 'shared'>(getInitialSelection);
  const [name, setName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<PickerFile | (PickerFile & DriveFile) | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sharedDriveUrl, setSharedDriveUrl] = useState('');
  const [sharedDriveError, setSharedDriveError] = useState('');

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value as 'new' | 'existing' | 'shared';
    setSelection(value);
    setSelectedFolder(null);
    setName('');
    setSharedDriveUrl('');
    setSharedDriveError('');
    const search = value === 'new' ? '' : `?${value}`;
    window.history.replaceState(null, '', `/app/create${search}`);
  };

  const openFolderPicker = async () => {
    await DrivePicker.load();
    DrivePicker.generatePicker({
      type: 'folder',
      onAction: (data) => {
        if (data.action !== 'picked') return;
        const pickerFile = data.docs[0];
        if (!pickerFile) throw new Error('file not defined');
        setSelectedFolder({ ...pickerFile });
        void DriveAPI.get({ id: pickerFile.id, fields: 'permissions', client: true })
          .catch(() => DriveAPI.get({ id: pickerFile.id }))
          .then((file) => {
            setSelectedFolder((prev) => (prev ? { ...prev, ...file } : null));
          })
          .catch(() => {
            setSelectedFolder((prev) => (prev ? { ...prev, permissions: [] } : null));
          });
      },
    });
    void DrivePicker.open('folder');
  };

  const hasYnawPermission =
    selectedFolder && 'permissions' in selectedFolder
      ? selectedFolder.permissions?.some((p) => p.type === 'user' && p.emailAddress === adminEmail)
      : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!rootFolder?.id) throw new Error('root folder is missing');

      if (selection === 'shared' && sharedDriveUrl.trim()) {
        const id = sharedDriveUrl.split('/').pop();
        if (!id || id.includes('/') || id.includes('\\') || !sharedDriveUrl.includes('folders')) {
          setSharedDriveError('Invalid shared drive URL');
          setIsSubmitting(false);
          return;
        }

        const file = await DriveAPI.get({ id }).catch(() => undefined);
        if (!file) {
          setSharedDriveError(`Drive not found. Please make sure ${adminEmail} has been added as a member.`);
          setIsSubmitting(false);
          return;
        }

        void fetch('/api/user/wikis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wikiId: id }),
        });
        void mutate(
          '/api/user/me',
          (prev: typeof user) => (prev ? { ...prev, wikis: [...(prev.wikis ?? []), id] } : prev),
          { revalidate: false }
        );
        void mutateWikis((wikis) => [...(wikis ?? []), file], { revalidate: false });
        navigate(`/app/page/${id}`);
        return;
      }

      if (selectedFolder) {
        if (!hasYnawPermission) {
          const updatedFile = await DriveAPI.get({ id: selectedFolder.id }).catch(() => undefined);
          if (!updatedFile) {
            setToast({
              type: 'error',
              message: `Add ${adminEmail} as an editor to the folder before continuing.`,
            });
            setIsSubmitting(false);
            return;
          }
          setSelectedFolder((prev) => (prev ? { ...prev, ...updatedFile } : null));
        }

        const shortcut = await DriveAPI.create({
          name: selectedFolder.name,
          mimeType: mimeTypes.shortcut,
          parents: [rootFolder.id],
          shortcutDetails: {
            targetId: selectedFolder.id,
          },
        });
        void mutateWikis((wikis) => [...(wikis ?? []), { ...selectedFolder, ...shortcut } as DriveFile], {
          revalidate: false,
        });
        navigate(`/app/page/${selectedFolder.id}`);
        return;
      }

      if (!name.trim()) throw new Error('name is missing');
      const folder = await DriveAPI.create({
        name: name.trim(),
        mimeType: mimeTypes.folder,
        parents: [rootFolder.id],
      });
      await DriveAPI.permissions.create({
        fileId: folder.id,
        role: 'writer',
        emailAddress: adminEmail,
        type: 'user',
      });
      void mutate(`/drive/v3/files/${folder.id}`, folder, { revalidate: false });
      navigate(`/app/page/${folder.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = match(selection)
    .with('new', () => !name.trim() || isSubmitting)
    .with('existing', () => !selectedFolder || !hasYnawPermission || isSubmitting)
    .with('shared', () => !sharedDriveUrl.trim() || isSubmitting)
    .exhaustive();

  return (
    <div className={formStyles.container}>
      <header className={formStyles.header}>
        {wikis.length > 0 && (
          <Link href="/app" className={inviteStyles.cancelLink}>
            Cancel
          </Link>
        )}
        {user && <AvatarMenu user={user} />}
      </header>
      <form className={formStyles.form} onSubmit={handleSubmit}>
        <Title style={{ fontSize: 42 }}>Create a wiki</Title>
        <p className={formStyles.description}>Create a new wiki or use an existing folder.</p>
        <FormControl className={styles.root} sx={{ m: 0, width: '100%' }}>
          <Select
            value={selection}
            onChange={handleChange}
            className={formStyles.select}
            MenuProps={{
              anchorOrigin: { vertical: 'top', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              sx: {
                '& .MuiMenuItem-root': {
                  padding: '12px 16px',
                },
              },
            }}
          >
            <MenuItem value="new">
              <ListItemText primary="Create a new folder" secondary="Creates a new folder in YNAW." />
            </MenuItem>
            <MenuItem value="existing">
              <ListItemText primary="Use an existing folder" secondary="Use your own folder to generate a wiki." />
            </MenuItem>
            <MenuItem value="shared">
              <ListItemText primary="Use a shared drive" secondary="Use a shared drive to generate a wiki." />
            </MenuItem>
          </Select>
        </FormControl>
        {selection === 'new' && (
          <>
            <h3 className={styles.sectionTitle}>Wiki name</h3>
            <input
              name="name"
              placeholder="E.g. My Wiki"
              className={formStyles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </>
        )}
        {selection === 'existing' && (
          <div className={styles.existingSection}>
            {!selectedFolder && (
              <button type="button" className={buttonStyles.secondary} onClick={openFolderPicker}>
                <img
                  style={{ width: 16, height: 16 }}
                  src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                  alt=""
                />
                Select a folder
              </button>
            )}
            {selectedFolder && (
              <>
                <div className={styles.selectedFolder}>
                  <Avatar sx={{ bgcolor: '#638AF5', width: 40, height: 40 }}>
                    <img
                      style={{ width: 22, filter: 'brightness(0) invert(1)' }}
                      src={selectedFolder.iconUrl?.replace(/\/\d+/, '/64')}
                      alt=""
                    />
                  </Avatar>
                  <div className={styles.selectedFolderInfo}>
                    <span className={styles.selectedFolderName}>{selectedFolder.name}</span>
                    <span className={styles.selectedFolderMeta}>
                      Last modified:{' '}
                      {new Date(
                        'modifiedTime' in selectedFolder && selectedFolder.modifiedTime
                          ? selectedFolder.modifiedTime
                          : selectedFolder.lastEditedUtc
                      ).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <IconButton
                    aria-label="Remove selection"
                    onClick={() => setSelectedFolder(null)}
                    sx={{ marginLeft: 'auto', color: '#9a9b9b' }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </div>
                {!hasYnawPermission && 'permissions' in selectedFolder && (
                  <div className={styles.permissionWarning}>
                    <InfoOutlined className={styles.infoIcon} />
                    <p className={styles.permissionWarningText}>
                      Add {adminEmail} as an editor to the folder to allow access.
                    </p>
                    <a
                      href={`https://drive.google.com/drive/folders/${selectedFolder.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={buttonStyles.secondary}
                    >
                      <img
                        style={{ width: 16, height: 16 }}
                        src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                        alt=""
                      />
                      Open in Drive
                      <OpenInNew style={{ marginLeft: 2, fontSize: 16 }} />
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {selection === 'shared' && (
          <div className={styles.existingSection}>
            <div className={styles.sharedDriveSteps}>
              <div className={styles.sharedDriveStep}>
                <NumberCircle n={1} className={styles.stepIcon} />
                <p className={styles.stepText}>Click view shared drives.</p>
              </div>
              <div className={styles.sharedDriveStep}>
                <NumberCircle n={2} className={styles.stepIcon} />
                <p className={styles.stepText}>
                  Add {adminEmail}
                  <CopyToClipboard
                    text={adminEmail}
                    containerStyle={{
                      width: 24,
                      height: 24,
                      marginLeft: 2,
                      marginRight: 2,
                      verticalAlign: 'middle',
                    }}
                    copyStyle={{ fontSize: 14 }}
                    checkStyle={{ fontSize: 14 }}
                  />
                  as a Manager to the shared drive to allow access.
                </p>
              </div>
              <div className={styles.sharedDriveStep}>
                <NumberCircle n={3} className={styles.stepIcon} />
                <p className={styles.stepText}>Copy and paste a shared drive URL into the input below.</p>
              </div>
            </div>
            <a
              href={`https://drive.google.com/drive/shared-drives${user?.email ? `?authuser=${user.email}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonStyles.secondary}
            >
              <img
                style={{ width: 16, height: 16 }}
                src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                alt=""
              />
              View shared drives
              <OpenInNew style={{ marginLeft: 2, fontSize: 16 }} />
            </a>
            <h3 className={styles.sectionTitle}>Shared drive URL</h3>
            <input
              name="sharedDriveUrl"
              placeholder="E.g. https://drive.google.com/drive/folders/{id}"
              className={formStyles.input}
              value={sharedDriveUrl}
              onChange={(e) => {
                setSharedDriveUrl(e.target.value);
                setSharedDriveError('');
              }}
            />
            {sharedDriveError && <p className={styles.errorText}>{sharedDriveError}</p>}
          </div>
        )}
        <div className={formStyles.actions}>
          <button type="submit" className={formStyles.primaryButton} disabled={isSubmitDisabled}>
            <span style={{ visibility: isSubmitting ? 'hidden' : 'visible' }}>Create</span>
            {isSubmitting && <CircularProgress size={16} className={styles.spinner} role="progressbar" />}
          </button>
          {wikis.length > 0 && (
            <Link href="/app" className={formStyles.cancelButton}>
              Cancel
            </Link>
          )}
        </div>
      </form>
    </div>
  );
};
